<template>
  <div class="space-y-4">
    <BasicOverview
      :booking="booking"
      show-status
      show-previous-bookings
      @has-changed="(changed) => (hasChanged.basicOverview = changed)"
    />

    <Divider />

    <BookingTimeline :booking="booking" />

    <Divider />

    <div v-if="!isLongTerm" class="grid items-start gap-4 md:grid-cols-3">
      <RangeInfos
        :booking="booking"
        :immutable="
          !$can('update', subject('Booking', booking), 'customInclusiveKm') ||
          disablePriceChangingModules('inclusiveKm')
        "
        @has-changed="(changed) => (hasChanged.inclusiveKm = changed)"
      />
    </div>

    <template v-if="isLongTerm">
      <PeriodTable :booking="booking" :invoices="invoices" />
      <CouponBenefitsSection :booking="booking" />
    </template>
    <template v-else>
      <Divider />

      <Extras
        :booking="booking"
        :disabled="disablePriceChangingModules('extras')"
        @has-changed="(changed) => (hasChanged.extras = changed)"
      />

      <Divider />

      <CustomExtras
        :booking="booking"
        :disabled="
          disablePriceChangingModules('customExtras') ||
          !$can('update', subject('Booking', booking), 'customBookedExtras')
        "
        :contract-penalty-disabled="
          disablePriceChangingModules('customExtras') ||
          !$can('update', subject('Booking', booking))
        "
        @has-changed="(changed) => (hasChanged.customExtras = changed)"
      />

      <CouponBenefitsSection :booking="booking" />
    </template>

    <Divider />

    <OtherConditions
      :booking="booking"
      @has-changed="(changed) => (hasChanged.otherConditions = changed)"
    />

    <template v-if="$can('view', subject('Booking', booking), 'agency')">
      <Divider />
      <Agency :booking="booking" />
    </template>

    <template v-if="$can('view', subject('Booking', booking), 'customer')">
      <Divider />
      <Customer :booking="booking" />
    </template>

    <template v-if="!isLongTerm">
      <Divider />

      <div v-show="!showAdditionalDrivers">
        <h2>{{ t('additionalDrivers') }}</h2>
        <CVButton class="mt-3 max-sm:w-full" @click.prevent="showAdditionalDrivers = true">{{
          t('showAdditionalDrivers')
        }}</CVButton>
      </div>

      <div v-show="showAdditionalDrivers">
        <AdditionalDrivers
          :booking="booking"
          :disabled="disablePriceChangingModules('additionalDrivers')"
          @has-changed="(changed) => (hasChanged.additionalDrivers = changed)"
        />
      </div>
    </template>

    <Divider />

    <template
      v-if="!isLongTerm && $can('get', 'Coupon') && $can('update', subject('Booking', booking))"
    >
      <CouponSection :booking="booking" />
      <Divider />
    </template>

    <Payments :booking="booking" :selectable-invoices="invoices" />

    <Divider />

    <BlockedDeposit :booking="booking" />

    <Divider />

    <div v-if="$can('read', 'Invoice')">
      <div class="flex items-center justify-between">
        <h2>{{ t('invoices') }}</h2>
        <CVButton
          v-if="$can('read', 'LedgerEntry')"
          variant="secondary"
          size="sm"
          @click="showLedgerEntriesModal = true"
        >
          {{ t('showLedgerEntries') }}
        </CVButton>
      </div>
      <Invoices :booking="booking" />

      <Divider />
    </div>

    <BillingSettings
      :booking="booking"
      @has-changed="(changed) => (hasChanged.billingSettings = changed)"
    />

    <Divider />

    <DamagesOfBookingList :booking="booking" />

    <Divider />

    <template v-if="hasInspections">
      <BookingInspections :booking="booking" />

      <Divider />
    </template>

    <div v-if="isAtLeastHandedOver">
      <BookingSummary :booking="booking" />

      <Divider />
    </div>

    <div class="mt-3 flex flex-col-reverse gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div class="flex gap-2 max-sm:w-full">
        <CancelButton
          v-if="
            $can(
              'update',
              subject('Booking', { ...booking, status: BookingStatus.CANCELED }),
              'status',
            ) &&
            booking.status !== BookingStatus.CAR_RETURNED &&
            booking.status !== BookingStatus.FINALIZED
          "
          :booking="booking"
          class="flex-1 sm:flex-initial"
        />
        <CVButton
          v-if="hasEligibleItemsToNet"
          variant="secondary"
          size="sm"
          class="max-sm:flex-1"
          :is-loading="isAutoNetPending"
          @click="executeAutoNet"
        >
          {{ t('netLedgerEntries') }}
        </CVButton>
      </div>
      <div class="self-end sm:justify-self-end">
        <BookingLedgerCostBreakdown :booking="booking" />
      </div>
    </div>

    <Modal v-model="showLedgerEntriesModal" :content-class="'max-w-6xl'" show-close>
      <template #title>{{ t('ledgerEntries') }}</template>
      <LedgerEntries :booking-id="booking.id" />
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import Divider from '@/components/Divider.vue';
import AdditionalDrivers from '@/components/booking-forms/AdditionalDrivers.vue';
import BasicOverview from '@/components/booking-forms/BasicOverview.vue';
import CouponBenefitsSection from '@/components/booking-forms/CouponBenefitsSection.vue';
import CustomExtras from '@/components/booking-forms/CustomExtras.vue';
import Extras from '@/components/booking-forms/Extras.vue';
import Invoices from '@/components/booking-forms/Invoices.vue';
import Payments from '@/components/booking-forms/Payments.vue';
import CouponSection from '@/components/booking-forms/CouponSection.vue';
import Customer from '@/components/booking-forms/customer/Customer.vue';
import DamagesOfBookingList from '@/components/booking-forms/damages/DamagesOfBookingList.vue';
import BookingInspections from '@/components/inspections/BookingInspections.vue';
import { useBookingInspections } from '@/queries/use-inspections';
import type { Booking } from '@/entities/bookings/booking.entity';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form';
import { useI18n } from 'vue-i18n';
import { computed, reactive, ref, watch } from 'vue';
import Agency from './components/Agency.vue';
import OtherConditions from '@/components/booking-forms/OtherConditions.vue';
import PeriodTable from '@/components/booking-forms/PeriodTable.vue';
import { isLongTermPricing } from '@/utils/pricing.utils';
import BookingSummary from '@/components/booking-forms/BookingSummary.vue';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import CancelButton from './components/CancelButton.vue';
import BlockedDeposit from '@/components/booking-forms/BlockedDeposit.vue';
import BookingLedgerCostBreakdown from './components/BookingLedgerCostBreakdown.vue';
import { subject } from '@casl/ability';
import BillingSettings from '@/components/booking-forms/BillingSettings.vue';
import BookingTimeline from '@/components/booking-timeline/BookingTimeline.vue';
import RangeInfos from '@/components/booking-forms/RangeInfos.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { useInvoicesOfBooking } from '@/hooks/use-invoices-of-booking.ts';
import LedgerEntries from '@/components/ledger-entries/LedgerEntries.vue';
import Modal from '@/components/Modal.vue';
import {
  useAutoNetBookingLedgerEntries,
  useCheckNettingEligibility,
} from '@/queries/use-ledger.ts';
import { Alert } from '@/services/alert.service';
import { useRoute } from 'vue-router';
import router from '@/router';
import { useAbility } from '@casl/vue';

const { t } = useI18n();
const route = useRoute();
const props = defineProps<{
  booking: Booking;
}>();

const hasChanged = reactive({
  extras: false,
  customExtras: false,
  basicOverview: false,
  inclusiveKm: false,
  totalBasePrice: false,
  otherConditions: false,
  additionalDrivers: false,
  billingSettings: false,
});

const showAdditionalDrivers = ref(false);
const showLedgerEntriesModal = ref(false);

const { data: inspections } = useBookingInspections(computed(() => props.booking.id));
const hasInspections = computed(() => !!inspections.value?.length);

router.afterEach(() => {
  showLedgerEntriesModal.value = false;
});

const isAtLeastHandedOver = computed(
  () =>
    props.booking.status === BookingStatus.HANDED_OVER ||
    props.booking.status === BookingStatus.CAR_RETURNED ||
    props.booking.status === BookingStatus.FINALIZED,
);
const isLongTerm = computed(() => isLongTermPricing(props.booking.priceCalculation));

const { invoices } = useInvoicesOfBooking(computed(() => props.booking.id));

const ability = useAbility();
const { data: nettingEligibility } = useCheckNettingEligibility(
  props.booking.id,
  ability.can('create', 'Netting'),
);

const hasEligibleItemsToNet = computed(() => {
  const eligibility = nettingEligibility.value;
  return eligibility?.CUSTOMER?.length || eligibility?.AGENCY?.length;
});

const { isPending: isAutoNetPending, mutateAsync: autoNetMutation } =
  useAutoNetBookingLedgerEntries();

const executeAutoNet = async () => {
  const { isConfirmed } = await Alert.fire({
    titleText: t('netLedgerEntriesConfirmTitle'),
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: t('netLedgerEntries'),
    cancelButtonText: t('cancel'),
  });
  if (isConfirmed) {
    await autoNetMutation(props.booking.id);
  }
};

const disablePriceChangingModules = (
  ignore: 'additionalDrivers' | 'customExtras' | 'extras' | 'inclusiveKm' | 'totalBasePrice',
) =>
  Object.entries({
    additionalDrivers: hasChanged.additionalDrivers,
    customExtras: hasChanged.customExtras,
    extras: hasChanged.extras,
    inclusiveKm: hasChanged.inclusiveKm,
    totalBasePrice: hasChanged.totalBasePrice,
  }).some(([key, value]) => key !== ignore && value);

usePreventLeavingUnsavedForm(() => Object.values(hasChanged).some((changed) => changed));

watch(
  () => route.query.paymentId,
  (paymentId) => {
    if (typeof paymentId === 'string' && paymentId) {
      showLedgerEntriesModal.value = false;
    }
  },
  { immediate: true },
);
</script>

<i18n lang="json">
{
  "en": {
    "noOption": "No",
    "additionalDrivers": "Additional Drivers",
    "showAdditionalDrivers": "Show additional Drivers",
    "bookedStartAndEndLocation": "Booked Start and End Location",
    "bookingOverview": "Booking Overview",
    "invoices": "Invoices",
    "showLedgerEntries": "Show Ledger Entries",
    "ledgerEntries": "Ledger Entries",
    "netLedgerEntries": "Net ledger entries",
    "netLedgerEntriesConfirmTitle": "Really net ledger entries?",
    "cancel": "Cancel"
  },
  "de": {
    "noOption": "Kein",
    "additionalDrivers": "Zusatzfahrer",
    "showAdditionalDrivers": "Zusatzfahrer anzeigen",
    "bookedStartAndEndLocation": "Gebuchter Start- und Endort",
    "bookingOverview": "Buchungsübersicht",
    "invoices": "Rechnungen",
    "showLedgerEntries": "Forderungseinträge anzeigen",
    "ledgerEntries": "Forderungseinträge",
    "netLedgerEntries": "Forderungseinträge verrechnen",
    "netLedgerEntriesConfirmTitle": "Forderungseinträge wirklich verrechnen?",
    "cancel": "Abbrechen"
  },
  "es": {
    "noOption": "No",
    "additionalDrivers": "Conductores adicionales",
    "showAdditionalDrivers": "Mostrar conductores adicionales",
    "bookedStartAndEndLocation": "Lugar de inicio y fin reservados",
    "bookingOverview": "Resumen de la reserva",
    "invoices": "Facturas",
    "showLedgerEntries": "Mostrar partidas contables",
    "ledgerEntries": "Partidas contables",
    "netLedgerEntries": "Compensar partidas (netting)",
    "netLedgerEntriesConfirmTitle": "¿Compensar las partidas?",
    "cancel": "Cancelar"
  }
}
</i18n>
